import type { SurveyTriggerConfig } from 'types';

/**
 * User-facing survey copy, shared by the library row menu and the details hub.
 *
 * Both surfaces offer the same lifecycle actions, so the wording that warns
 * about them lives in one place. Duplicating it meant the two could drift and a
 * visitor would be told two different things about the same button.
 */

export type SurveyAction = 'start' | 'end' | 'duplicate' | 'archive' | 'delete';

export const SURVEY_ACTION_PROMPTS: Record<SurveyAction, { title: string; body: string }> = {
  start: {
    title: 'Start this survey?',
    body: 'It goes live on this website straight away and starts collecting responses.',
  },
  end: {
    title: 'End this survey?',
    body: 'It stops showing on this website straight away. Existing responses are kept.',
  },
  duplicate: { title: 'Duplicate this survey?', body: 'A copy is created as a draft. Responses are not copied.' },
  archive: { title: 'Archive this survey?', body: 'It is hidden from the default view. This can be undone.' },
  delete: {
    title: 'Delete this survey?',
    body: 'The survey is removed from the library. Responses already collected are kept so past results stay readable.',
  },
};

export const TRIGGER_LABEL: Record<string, string> = {
  'page view': 'On page view',
  delay: 'After a delay',
  'exit intent': 'On exit intent',
  event: 'On event',
  'scroll depth': 'At scroll depth',
};

/** The trigger label plus whatever the trigger's own config adds to it. */
export const describeTrigger = (config: SurveyTriggerConfig): string => {
  if (config.type === 'delay') return `After a delay of ${config.seconds}s`;
  if (config.type === 'scroll depth') return `At ${config.percent}% scroll depth`;
  if (config.type === 'event') return `On event "${config.eventName}"`;
  return TRIGGER_LABEL[config.type] ?? config.type;
};
